import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { formatDate } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Eye } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";

export default function NewsEditor() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);

  const [form, setForm] = useState({
    title: "", excerpt: "", content: "", image_path: null, is_published: false, publish_at: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/news/${id}`).then((r) => {
      const d = r.data;
      setForm({
        title: d.title || "",
        excerpt: d.excerpt || "",
        content: d.content || "",
        image_path: d.image_path || null,
        is_published: !!d.is_published,
        publish_at: d.publish_at ? d.publish_at.slice(0, 16) : "",
      });
    });
  }, [id, isEdit]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null };
      if (isEdit) {
        await api.put(`/news/${id}`, payload);
        toast.success(t("admin.news.toastUpdated"));
      } else {
        await api.post("/news", payload);
        toast.success(t("admin.news.toastCreated"));
      }
      navigate("/admin/news");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="news-editor" className="space-y-8 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin/news" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-bordeaux mb-3">
            <ArrowLeft size={14} /> {t("admin.news.backList")}
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            {isEdit ? t("admin.news.editorEdit") : t("admin.news.editorNew")}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => setShowPreview((s) => !s)} data-testid="preview-toggle-btn" className="rounded-sm border-slate-300 hover:bg-beige-light hover:text-slate-900">
            <Eye size={14} className="mr-2" /> {showPreview ? t("admin.news.editMode") : t("admin.news.previewMode")}
          </Button>
        </div>
      </div>

      {showPreview ? (
        <article data-testid="preview-content" className="bg-white border border-slate-200 rounded-sm p-8 md:p-12 break-words">
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-4">
            {formatDate(form.publish_at) || t("admin.news.previewMode")}
            {!form.is_published && ` · ${t("admin.news.previewDraft")}`}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight break-words">
            {form.title || t("admin.news.previewPlaceholderTitle")}
          </h1>
          {form.excerpt && <p className="mt-4 text-lg text-slate-600 break-words">{form.excerpt}</p>}
          {form.image_path && (
            <div className="mt-8 rounded-sm overflow-hidden border border-slate-200">
              <img src={fileUrl(form.image_path)} alt={form.title} className="w-full h-auto" />
            </div>
          )}
          <div className="mt-8 whitespace-pre-line break-words text-slate-700 leading-relaxed">{form.content}</div>
        </article>
      ) : (
        <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-sm p-6 md:p-8 space-y-6">
          <Field label={t("admin.news.fieldTitle")}>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("admin.news.fieldTitlePlaceholder")} data-testid="news-title-input" className="rounded-sm border-slate-300" />
          </Field>
          <Field label={t("admin.news.fieldExcerpt")}>
            <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder={t("admin.news.fieldExcerptPlaceholder")} data-testid="news-excerpt-input" className="rounded-sm border-slate-300" />
          </Field>
          <Field label={t("admin.news.fieldContent")}>
            <Textarea required rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder={t("admin.news.fieldContentPlaceholder")} data-testid="news-content-input" className="rounded-sm border-slate-300" />
          </Field>
          <Field label={t("admin.news.fieldImage")}>
            <ImageUploader
              value={form.image_path}
              onChange={(p) => setForm((prev) => ({ ...prev, image_path: p }))}
              testId="news-image-uploader"
            />          </Field>

          <div className="grid sm:grid-cols-2 gap-6 pt-2">
            <div className="flex items-center justify-between bg-beige-light border border-slate-200 rounded-sm p-4">
              <div>
                <div className="font-medium text-slate-900">{t("admin.news.publishedToggle")}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t("admin.news.publishedDesc")}</div>
              </div>
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="news-published-switch" />
            </div>
            <Field label={t("admin.news.publishAt")}>
              <Input type="datetime-local" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} data-testid="news-publish-at-input" className="rounded-sm border-slate-300" />
            </Field>
          </div>

          <div className="pt-4 flex items-center gap-3">
            <Button type="submit" disabled={busy} data-testid="news-save-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
              <Save size={14} className="mr-2" /> {isEdit ? t("admin.common.save") : t("admin.common.create")}
            </Button>
            <Link to="/admin/news" className="text-sm text-slate-500 hover:text-slate-900">{t("admin.common.cancel")}</Link>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
